/** @format */

import { Grid } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentBlock } from './contentBlock';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'src/store';
import { getPublicContentInfo } from 'src/slices/publicContent';
import { getPrivateContentInfo } from 'src/slices/privateContent';
import { getFooterLinksInfo } from 'src/slices/global';
import { Navigate, useSearchParams } from 'react-router-dom';
import i18n from '../../i18n';
import useAuth from 'src/hooks/useAuth';
import { getAppLanguage, setAppLanguage } from 'src/utils/locale';

import Breadcrumb from 'src/components/Breadcrumb';
import Page from 'src/components/Page';
import { setupCmsEmbedPageEffects } from 'src/utils/cmsEmbed';
import type { CmsPageContent } from 'src/types';
import { CloseIcon, SearchIcon } from 'src/components/icons';
import { CardList, filterCardList, isCardListContent } from './CardList';
import { LegalPageSkeleton } from './LegalPageSkeleton';

const LegalLayout = (props: Record<string, unknown>) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pageId, type } = useParams();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearch = searchParams.get('search') ?? '';
  const [contentInfo, setContentInfo] = useState<CmsPageContent>({ title: '' });
  const [loadedPageId, setLoadedPageId] = useState<string | null>(null);
  const [cardQuery, setCardQuery] = useState(urlSearch);
  const [, setLang] = useState<string | undefined>();
  const { userData } = useSelector(state => state.users);
  const { error } = useSelector(state => state.global);
  const { privateContentInfo, error: privateContentError } = useSelector(state => state.privateContent);
  const { error: publicContentError } = useSelector(state => state.publicContent);
  const cachedContent = pageId && type !== 'public' ? privateContentInfo[pageId] : undefined;

  useEffect(() => {
    const lang = getAppLanguage();
    const cached = pageId && type !== 'public' ? privateContentInfo[pageId] : undefined;
    if (!cached) setContentInfo({ title: '' });

    if (!pageId) return undefined;

    let active = true;
    const handleSuccess = (res: unknown) => {
      if (!active) return;
      setContentInfo(res as CmsPageContent);
      setLoadedPageId(pageId);
    };
    const handleFailure = () => {
      if (!active) return;
      setLoadedPageId(pageId);
    };

    if (type === 'public') {
      if (user) {
        dispatch(getPublicContentInfo(pageId, handleSuccess, handleFailure));
        setLang(lang);
      } else {
        dispatch(getPublicContentInfo(pageId, handleSuccess, handleFailure));
        dispatch(getFooterLinksInfo());
      }
    } else {
      dispatch(getPrivateContentInfo(pageId, handleSuccess, handleFailure));
    }

    return () => {
      active = false;
    };
    // eslint-disable-next-line
  }, [pageId]);

  useEffect(() => {
    const lang = getAppLanguage();
    if (user) {
      setLang(lang);
      i18n.changeLanguage(lang);
    } else {
      setLang(lang);
    }
  }, [props, user]);

  useEffect(() => {
    setCardQuery(urlSearch);
  }, [urlSearch]);

  const loading = Boolean(pageId) && loadedPageId !== pageId;
  const displayedContent = loading && cachedContent ? cachedContent : contentInfo;
  const visibleCards = useMemo(
    () => filterCardList(displayedContent.cards || [], cardQuery),
    [cardQuery, displayedContent.cards]
  );

  const submitCardSearch = (value: string) => {
    const next = new URLSearchParams(searchParams);
    const trimmed = value.trim();
    if (trimmed) next.set('search', trimmed);
    else next.delete('search');
    setSearchParams(next);
  };

  const clearCardSearch = () => {
    setCardQuery('');
    submitCardSearch('');
  };

  useEffect(() => {
    if (!userData) return undefined;
    return setupCmsEmbedPageEffects(displayedContent?.body || '', { userData });
  }, [displayedContent?.body, userData]);

  if (error || privateContentError || publicContentError) return <Navigate to="/404" replace />;

  const pageTitle = displayedContent?.title || '';
  const cardList = isCardListContent(displayedContent);
  const showCachedContent = loading && Boolean(cachedContent);
  const documentTitle = showCachedContent || !loading ? pageTitle : t('common.loading');

  return (
    <Page className={`archive-page news-page legal-page`} title={documentTitle}>
      {loading && !cachedContent ? (
        <LegalPageSkeleton label={t('common.loading')} />
      ) : (
        <>
          <Breadcrumb data={[{ title: pageTitle }]} additionalClasses={cardList ? undefined : 'container--narrow'} />
          <Grid style={{ viewTransitionName: 'news-detail' }}>
            <div className={`news-detail-content${showCachedContent ? ' news-detail-content--loading' : ''}`} aria-busy={showCachedContent}>
              {cardList ? (
                <div className="content-container container card-list-page">
                  <div className="archive-page-title-box">
                    <h1 className="archive-page-title">{pageTitle}</h1>
                    <div className="card-list-page__search">
                      <form
                        className="keyword-search"
                        onSubmit={event => {
                          event.preventDefault();
                          submitCardSearch(cardQuery);
                        }}>
                        <div className={`keyword-search__field${cardQuery ? ' card-list-page__search-field--filled' : ''}`}>
                          <input
                            type="search"
                            className="search-page__query-input"
                            value={cardQuery}
                            onChange={event => setCardQuery(event.target.value)}
                            onSearch={event => {
                              if (event.currentTarget.value !== '') return;
                              clearCardSearch();
                            }}
                            onKeyDown={event => {
                              if (event.key !== 'Enter' && event.key !== 'NumpadEnter') return;
                              event.preventDefault();
                              submitCardSearch(cardQuery);
                            }}
                            placeholder={t('administrationPage.extranetContents.searchPlaceholder')}
                            aria-label={t('administrationPage.extranetContents.searchPlaceholder')}
                          />
                          {cardQuery ? (
                            <button
                              type="button"
                              className="card-list-page__search-clear"
                              onClick={clearCardSearch}
                              aria-label={t('searchPage.removeKeywordAria', { keyword: cardQuery.trim() })}>
                              <CloseIcon />
                            </button>
                          ) : null}
                          <button type="submit" className="search-page__query-submit" aria-label={t('common.search')}>
                            <SearchIcon />
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                  <CardList cards={visibleCards} />
                </div>
              ) : (
                <Grid className="archive-page-title-box container container--narrow">
                  <h1 className="content-page-title news-page-title">{pageTitle}</h1>
                </Grid>
              )}
              {cardList ? null : <ContentBlock content={displayedContent} />}
            </div>
          </Grid>
        </>
      )}
    </Page>
  );
};

export { LegalLayout };
